package com.dotdrives.web.pages.donor

import com.dotdrives.controller.DonorController
import com.dotdrives.controller.DonorUiState
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.*
import com.dotdrives.web.ui.DotButton
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.article
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.footer
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.header
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.strong
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType
import web.html.text

external interface DonorPortalProps : Props {
    var state: DonorUiState
    var controller: DonorController
    var email: String
    var hasTeam: Boolean
    var onTeam: () -> Unit
    var onSignOut: () -> Unit
}

val DonorPortalPage =
    FC<DonorPortalProps> { props ->
        val s = props.state
        val c = props.controller
        val (managing, setManaging) = useState<String?>(null)
        val (profileOpen, setProfileOpen) = useState(false)
        val (activeSection, setSection) = useState("Overview")
        useEffect(s.orgId) {
            setManaging(null)
            setProfileOpen(false)
        }
        DonorStyles()
        main {
            className = ClassName("donor-portal")
            header {
                className = ClassName("donor-header")
                div {
                    className = ClassName("donor-brand")
                    span {
                        className = ClassName("donor-mark")
                        +(s.organizations.firstOrNull { it.id == s.orgId }?.name?.take(1) ?: "d")
                    }
                    strong {
                        +(s.organizations.firstOrNull { it.id == s.orgId }?.name ?: "Dot Drives")
                    }
                    span {
                        className = ClassName("donor-meta")
                        +"MY GIVING"
                    }
                }
                if (s.snapshot != null)
                    nav {
                        listOf("Overview", "Gifts & receipts", "Recurring", "Updates").forEach {
                            name ->
                            button {
                                type = ButtonType.buttonType
                                className = ClassName(if (activeSection == name) "active" else "")
                                onClick = { setSection(name) }
                                +name
                            }
                        }
                    }
                div {
                    className = ClassName("donor-account")
                    if (s.snapshot != null)
                        button {
                            type = ButtonType.buttonType
                            onClick = { setProfileOpen(!profileOpen) }
                            +"Profile"
                        }
                    if (props.hasTeam)
                        button {
                            type = ButtonType.buttonType
                            onClick = { props.onTeam() }
                            +"Team workspace"
                        }
                    button {
                        type = ButtonType.buttonType
                        onClick = { props.onSignOut() }
                        +"Sign out"
                    }
                }
            }
            div {
                className = ClassName("donor-content")
                if (s.error != null)
                    div {
                        className = ClassName("donor-error")
                        +s.error
                        button {
                            type = ButtonType.buttonType
                            onClick = {
                                if (s.pendingOperation != null) c.retryRecurring() else c.refresh()
                            }
                            +"Retry"
                        }
                    }
                s.message?.let {
                    p {
                        className = ClassName("donor-message")
                        +it
                        button {
                            type = ButtonType.buttonType
                            onClick = { c.refresh() }
                            +"Check latest status"
                        }
                    }
                }
                if (s.loading) {
                    div {
                        className = ClassName("donor-loading")
                        +"Loading your giving…"
                    }
                } else if (s.orgId == null) {
                    h1 { +"Your giving, all in one place" }
                    p { +"Signed in as ${props.email}" }
                    if (s.organizations.isEmpty()) {
                        section {
                            className = ClassName("donor-panel donor-empty")
                            h2 { +"No giving records found" }
                            p {
                                +"This verified email does not match an available giving account. Use the email you gave with, or contact your organization to check its records."
                            }
                            DotButton {
                                text = "Check again"
                                onClick = c::start
                            }
                        }
                    } else {
                        p { +"Choose an organization to view your gifts." }
                        s.organizations.forEach { org ->
                            div {
                                className = ClassName("donor-panel donor-org")
                                h2 { +org.name }
                                DotButton {
                                    text = "View my giving"
                                    onClick = { c.selectOrg(org.id) }
                                }
                            }
                        }
                    }
                } else
                    s.snapshot?.let { snapshot ->
                        div {
                            className = ClassName("donor-welcome")
                            h1 { +"Welcome back, ${snapshot.profile.displayName}" }
                            p { +"Your gifts, receipts, and updates — together in one place." }
                            if (s.organizations.size > 1)
                                button {
                                    type = ButtonType.buttonType
                                    onClick = { c.selectOrg(null) }
                                    +"Switch organization"
                                }
                        }
                        if (profileOpen)
                            DonorContact {
                                key = s.orgId.unsafeCast<Key>()
                                profile = snapshot.profile
                                busy = s.actionBusy
                                onSave = c::saveContact
                                onClose = { setProfileOpen(false) }
                            }
                        if (activeSection == "Overview")
                            section {
                                className = ClassName("donor-summary-grid")
                                div {
                                    className = ClassName("donor-panel donor-lifetime")
                                    p {
                                        className = ClassName("donor-meta")
                                        +"YOUR LIFETIME GIVING"
                                    }
                                    div {
                                        className = ClassName("donor-total")
                                        +donorMoney(snapshot.summary.lifetime)
                                    }
                                    p {
                                        +"${snapshot.summary.giftCount} gifts · ${snapshot.summary.causeCount} recorded ${if(snapshot.summary.causeCount == 1) "cause" else "causes"}"
                                    }
                                    p {
                                        +(snapshot.summary.firstGiftAt?.let {
                                            "Giving since ${donorDate(it)}"
                                        } ?: "Your first gift will appear here")
                                    }
                                    div {
                                        className = ClassName("donor-ytd")
                                        +"This year: ${donorMoney(snapshot.summary.yearTotal)}"
                                    }
                                }
                                div {
                                    className = ClassName("donor-panel donor-recurring")
                                    p {
                                        className = ClassName("donor-meta")
                                        +"RECURRING GIVING"
                                    }
                                    snapshot.subscriptions.firstOrNull()?.let { plan ->
                                        h2 {
                                            +"${donorMoney(plan.amount)} / ${if (plan.interval == "monthly") "month" else plan.interval}"
                                        }
                                        p { +subscriptionStatus(plan) }
                                        p {
                                            +(plan.nextChargeAt?.let {
                                                "Next charge ${donorDate(it)}"
                                            }
                                                ?: if (plan.status in setOf("paused", "canceled"))
                                                    "No next charge scheduled"
                                                else "Next charge date unavailable")
                                        }
                                        DotButton {
                                            text = "Manage"
                                            onClick = {
                                                setSection("Recurring")
                                                setManaging(plan.id)
                                            }
                                        }
                                    }
                                        ?: run {
                                            h2 { +"Give at your own pace" }
                                            p {
                                                +"You have no recurring gifts with this organization."
                                            }
                                        }
                                }
                                div {
                                    className = ClassName("donor-panel")
                                    h2 { +"Annual receipts" }
                                    ReceiptList {
                                        this.state = s
                                        controller = c
                                    }
                                }
                            }
                        if (activeSection == "Recurring") {
                            h2 { +"Your recurring gifts" }
                            if (snapshot.subscriptions.isEmpty())
                                p { +"No recurring gifts to manage." }
                            snapshot.subscriptions.forEach { plan ->
                                section {
                                    className = ClassName("donor-panel donor-recurring")
                                    h2 {
                                        +"${donorMoney(plan.amount)} / ${if (plan.interval == "monthly") "month" else plan.interval}"
                                    }
                                    p { +subscriptionStatus(plan) }
                                    p {
                                        +(plan.nextChargeAt?.let { "Next charge ${donorDate(it)}" }
                                            ?: if (plan.status in setOf("paused", "canceled"))
                                                "No next charge scheduled"
                                            else "Next charge date unavailable")
                                    }
                                    p {
                                        +"Payment details are managed securely with the payment provider."
                                    }
                                    DotButton {
                                        text =
                                            if (managing == plan.id) "Close management"
                                            else "Manage recurring gift"
                                        onClick = {
                                            setManaging(if (managing == plan.id) null else plan.id)
                                        }
                                    }
                                    if (managing == plan.id)
                                        DonorRecurringControls {
                                            this.plan = plan
                                            this.state = s
                                            controller = c
                                        }
                                }
                            }
                        }
                        if (
                            activeSection == "Overview" ||
                                activeSection == "Gifts & receipts" ||
                                activeSection == "Updates"
                        )
                            div {
                                className =
                                    ClassName(
                                        if (activeSection == "Overview") "donor-detail-grid"
                                        else "donor-detail-single"
                                    )
                                if (activeSection != "Updates")
                                    section {
                                        className = ClassName("donor-panel")
                                        h2 { +"Gift history" }
                                        p {
                                            className = ClassName("donor-secondary")
                                            +"Most recent first · reversals are included in totals"
                                        }
                                        if (snapshot.donations.isEmpty())
                                            p { +"No gifts recorded yet." }
                                        snapshot.donations.forEach { gift ->
                                            div {
                                                className = ClassName("donor-gift")
                                                span {
                                                    className = ClassName("donor-gift-icon")
                                                    +(if (gift.amount < 0) "↶" else "♥")
                                                }
                                                div {
                                                    strong {
                                                        +(if (gift.reversesDonationId != null)
                                                            "Gift reversal"
                                                        else "Gift received")
                                                    }
                                                    p {
                                                        +"${donorPaymentLabel(gift.paymentType)} · ${donorSourceLabel(gift.source)}"
                                                    }
                                                }
                                                span {
                                                    className = ClassName("donor-date")
                                                    +donorDate(gift.occurredAt)
                                                }
                                                strong {
                                                    className =
                                                        ClassName(
                                                            if (gift.amount < 0)
                                                                "donor-money reversal"
                                                            else "donor-money"
                                                        )
                                                    +donorMoney(gift.amount)
                                                }
                                            }
                                        }
                                        if (snapshot.nextAfter != null)
                                            DotButton {
                                                text =
                                                    if (s.moreBusy) "Loading…"
                                                    else "Load older gifts"
                                                enabled = !(s.moreBusy)
                                                variant = DotButtonVariant.Secondary
                                                onClick = c::loadMore
                                            }
                                        if (activeSection == "Gifts & receipts") {
                                            h2 { +"Annual receipts" }
                                            ReceiptList {
                                                this.state = s
                                                controller = c
                                            }
                                        }
                                    }
                                if (activeSection != "Gifts & receipts")
                                    section {
                                        className = ClassName("donor-panel")
                                        h2 { +"Updates from the field" }
                                        if (s.updates.isEmpty())
                                            p { +"Updates shared with you will appear here." }
                                        s.updates.forEach { report ->
                                            article {
                                                className = ClassName("donor-update")
                                                h3 {
                                                    if (report.openedAt == null)
                                                        span {
                                                            className = ClassName("donor-unread")
                                                            +"● "
                                                        }
                                                    +report.title
                                                }
                                                p {
                                                    +(report.sharedAt?.let {
                                                        "Shared ${donorDate(it)}"
                                                    } ?: "Shared with you")
                                                }
                                                if (s.openUpdateId == report.id) {
                                                    report.updates.forEach { body ->
                                                        p {
                                                            className =
                                                                ClassName("donor-update-body")
                                                            +body.body
                                                        }
                                                    }
                                                    report.attachments.forEach { file ->
                                                        button {
                                                            type = ButtonType.buttonType
                                                            disabled = s.actionBusy
                                                            onClick = { c.attachment(file.id) }
                                                            +"Download ${file.name}"
                                                        }
                                                    }
                                                    button {
                                                        type = ButtonType.buttonType
                                                        onClick = { c.closeUpdate() }
                                                        +"Close update"
                                                    }
                                                } else
                                                    button {
                                                        type = ButtonType.buttonType
                                                        disabled = s.actionBusy
                                                        onClick = { c.openUpdate(report.id) }
                                                        +"Read update →"
                                                    }
                                            }
                                        }
                                    }
                            }
                        if (s.pendingOperation != null)
                            div {
                                className = ClassName("donor-panel")
                                h3 { +"A management request needs your attention" }
                                p {
                                    +"${s.pendingOperation?.action.orEmpty().replace('_',' ')} request saved. "
                                    s.pendingOperation?.amount?.let {
                                        +"Requested full monthly charge: USD $it. "
                                    }
                                    s.pendingOperation?.day?.let {
                                        +"Requested billing day: $it (UTC). "
                                    }
                                    +"Retry uses the same exact request; other changes remain unavailable until it resolves."
                                }
                                DotButton {
                                    text = "Retry saved request"
                                    enabled = !(s.actionBusy)
                                    onClick = c::retryRecurring
                                }
                            }
                        if (s.document != null || s.documentBusy)
                            div {
                                className = ClassName("donor-panel donor-document")
                                h3 { +"Document download" }
                                when (s.document?.status) {
                                    "ready" -> {
                                        a {
                                            href = s.document?.url
                                            target = "_blank".unsafeCast<web.window.WindowTarget>()
                                            rel = "noopener noreferrer"
                                            +"Open PDF or attachment"
                                        }
                                        p {
                                            +"This private link expires after five minutes. Request the download again for a fresh link."
                                        }
                                    }
                                    "failed" -> {
                                        p { +"The document could not be prepared." }
                                        if (s.document?.retryable == true)
                                            DotButton {
                                                text = "Retry document"
                                                enabled = !(s.documentBusy)
                                                onClick = {
                                                    s.documentId?.let { c.document(it, true) }
                                                }
                                            }
                                    }
                                    else -> {
                                        p {
                                            +(if (s.documentBusy) "Preparing your document…"
                                            else
                                                "The document is still being prepared. Check again when ready.")
                                        }
                                        if (!s.documentBusy)
                                            DotButton {
                                                text = "Check document"
                                                onClick = { s.documentId?.let { c.document(it) } }
                                            }
                                    }
                                }
                            }
                    }
                footer { +"Your giving is private · Powered by Dot Drives" }
            }
        }
    }
